'use client';

import ReactPlayer from 'react-player';

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg|avif)(\?.*)?$/i;
const VIDEO_EXTENSIONS = /\.(mp4|webm|ogv|m4v|mov)(\?.*)?$/i;
const STREAM_EXTENSIONS = /\.(m3u8|mpd)(\?.*)?$/i;
const AUDIO_EXTENSIONS = /\.(mp3|m4a|aac|wav|oga|ogg|flac)(\?.*)?$/i;
const EMBED_CODE =
    /<\s*(iframe|script|video|svg|div|section|figure|blockquote)[\s>]/i;
const FACEBOOK_HOSTS = /(facebook\.com|fb\.watch)/i;
const PROVIDER_HINTS =
    /(youtube\.com|youtu\.be|vimeo\.com|facebook\.com|fb\.watch|tiktok\.com|soundcloud\.com|mixcloud\.com|twitch\.tv|dailymotion\.com|streamable\.com|wistia\.com|vidyard\.com|kaltura\.com)/i;

export type FacebookEmbedOptions = {
    autoplay?: boolean;
    muted?: boolean;
    width?: number;
    height?: number;
    allowFullScreen?: boolean;
    showText?: boolean;
    showCaptions?: boolean;
    playsinline?: boolean;
};

export function isVideoUrl(url: unknown): boolean {
    if (!url || typeof url !== 'string') return false;

    const trimmed = url.trim();
    if (!trimmed || EMBED_CODE.test(trimmed)) return false;
    if (IMAGE_EXTENSIONS.test(trimmed)) return false;
    if (VIDEO_EXTENSIONS.test(trimmed)) return true;
    if (STREAM_EXTENSIONS.test(trimmed)) return true;
    if (AUDIO_EXTENSIONS.test(trimmed)) return true;
    if (PROVIDER_HINTS.test(trimmed)) return true;

    if (typeof ReactPlayer?.canPlay === 'function') {
        return ReactPlayer.canPlay(trimmed);
    }

    return false;
}

export function isFacebookVideoUrl(url: unknown): boolean {
    if (!url || typeof url !== 'string') return false;
    return FACEBOOK_HOSTS.test(url.trim());
}

export function getFacebookEmbedUrl(
    url: unknown,
    options: FacebookEmbedOptions = {}
): string {
    if (!url || typeof url !== 'string') return '';

    const trimmed = url.trim();
    if (!trimmed) return '';
    if (/facebook\.com\/plugins\/video\.php/i.test(trimmed)) return trimmed;

    const autoplay = options.autoplay ? '1' : '0';
    const mute = options.muted ? '1' : '0';
    const width = Number.isFinite(options.width) ? options.width : 1280;
    const height = Number.isFinite(options.height) ? options.height : null;
    const allowFullScreen = options.allowFullScreen ? '1' : '0';
    const showText = options.showText ? '1' : '0';
    const showCaptions = options.showCaptions ? '1' : '0';
    const playsInline = options.playsinline ? '1' : '0';

    const params = new URLSearchParams({
        href: trimmed,
        show_text: showText,
        show_captions: showCaptions,
        autoplay,
        mute,
        width: String(width),
        allowfullscreen: allowFullScreen,
        playsinline: playsInline,
    });

    if (height) params.set('height', String(height));

    return `https://www.facebook.com/plugins/video.php?${params.toString()}`;
}
